import { useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
	DARK_MODE,
	ENGRAVING_FILE_CREATION_ERROR,
	forbiddenChars,
	PRINTER_ERROR,
} from '../../constants';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import { axiosInstance } from '../../api/api';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import LargeButton from '../LargeButton/LargeButton';
import { BasicTable } from '..';
import { IconButton } from '../../atomicComponents';
import CloseIcon from '@mui/icons-material/Close';

const folder_name = 'IPMS';
const file_name = 'VIN';

const DEFAULT_COLUMNS = [
	{
		width: 210,
		title: '',
		field: 'serialNumber',
	},
];

/**
 * Enhanced Re-engrave Modal - JLR Luxury Theme
 * 
 * Features:
 * - Glass-morphism design with gradient backgrounds
 * - High-contrast text for better readability
 * - JLR color scheme (racing blue accents)
 * - Enhanced typography and spacing
 * - Consistent with other modals in the application
 */

const classNames = {
	dialogCointainer: {
		background: (theme) => theme.palette.mode === DARK_MODE 
			? 'rgba(0, 0, 0, 0.7)' 
			: 'rgba(45, 74, 45, 0.5)',
		backdropFilter: 'blur(8px)',
	},
	dialog: {
		padding: '2rem',
		width: '900px',
		maxWidth: '90vw',
		background: (theme) => theme.palette.mode === 'dark'
			? 'linear-gradient(135deg, #1a2d1a 0%, #2d4a2d 50%, #1a2d1a 100%)'
			: 'linear-gradient(135deg, #e8f2e8 0%, #c5d9c5 50%, #e8f2e8 100%)',
		borderRadius: '20px',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(45, 74, 45, 0.4)'
			: '1px solid rgba(45, 74, 45, 0.3)',
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 16px 48px rgba(0, 0, 0, 0.4), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
			: '0px 16px 48px rgba(45, 74, 45, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.3)',
		position: 'relative',
		overflow: 'hidden',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: '0',
			left: '0',
			right: '0',
			bottom: '0',
			background: (theme) => theme.palette.mode === 'dark'
				? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 100%)'
				: 'linear-gradient(135deg, rgba(45, 74, 45, 0.03) 0%, rgba(45, 74, 45, 0.01) 50%, rgba(45, 74, 45, 0.03) 100%)',
			borderRadius: '20px',
			pointerEvents: 'none',
		}
	},
	dialogContent: {
		backgroundColor: 'transparent',
		maxHeight: '600px',
		padding: '1.5rem',
	},
	content: {
		padding: '2.5rem', // More generous padding for professional spacing
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: (theme) => theme.palette.mode === 'dark'
			? 'linear-gradient(135deg, rgba(45, 74, 45, 0.25) 0%, rgba(45, 74, 45, 0.15) 50%, rgba(45, 74, 45, 0.25) 100%)'
			: 'linear-gradient(135deg, rgba(45, 74, 45, 0.12) 0%, rgba(45, 74, 45, 0.06) 50%, rgba(45, 74, 45, 0.12) 100%)',
		borderRadius: '18px', // More rounded for modern look
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(76, 175, 80, 0.2)' // Subtle green border to match theme
			: '1px solid rgba(76, 175, 80, 0.15)',
		marginTop: '1rem', // Better spacing from title
	},
	noteHeading: {
		color: (theme) => theme.palette.mode === 'dark'
			? '#ffffff' // High contrast white for dark mode
			: '#1a1a1a', // High contrast dark for light mode
		fontSize: pxToRem(14),
		...responsiveFontSizes({ sm: 13, md: 15, lg: 18 }),
		fontWeight: 600,
		textShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 2px 4px rgba(0, 0, 0, 0.4)'
			: '0px 2px 4px rgba(0, 0, 0, 0.2)',
	},
	heading: {
		color: (theme) => theme.palette.mode === 'dark'
			? '#ffffff' // High contrast white for dark mode
			: '#1a1a1a', // High contrast dark for light mode
		fontSize: pxToRem(14),
		...responsiveFontSizes({ sm: 14, md: 16, lg: 20 }),
		fontWeight: 700,
		textShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 2px 4px rgba(0, 0, 0, 0.4)'
			: '0px 2px 4px rgba(0, 0, 0, 0.2)',
	},
	item: {
		paddingTop: '0.5rem',
		paddingBottom: '0.5rem',
	},
	titleLine: {
		flex: 1,
		height: '6px', // Made thicker for better visual presence
		borderRadius: '3px',
		background: (theme) => theme.palette.mode === 'dark'
			? 'linear-gradient(90deg, #4CAF50 0%, #2E7D32 50%, #4CAF50 100%)' // Professional green gradient
			: 'linear-gradient(90deg, #2E7D32 0%, #4CAF50 50%, #2E7D32 100%)',
		marginLeft: 4, // Better spacing alignment
		marginRight: 2, // Added for balanced spacing
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 3px 12px rgba(76, 175, 80, 0.5)' // Green glow effect
			: '0px 3px 12px rgba(46, 125, 50, 0.4)',
	},
	actionBtnContainer: {
		paddingX: 4,
		paddingY: 2,
	},
	cancelBtn: {
		color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
		background: 'transparent',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(255, 255, 255, 0.2)'
			: '1px solid rgba(26, 26, 26, 0.2)',
		borderRadius: '12px',
		padding: '0.75rem 1.5rem',
		fontWeight: 600,
		textTransform: 'none',
		transition: 'all 0.3s ease',
		'&:hover': {
			background: (theme) => theme.palette.mode === 'dark'
				? 'rgba(255, 255, 255, 0.1)'
				: 'rgba(26, 26, 26, 0.05)',
			transform: 'translateY(-2px)',
			boxShadow: (theme) => theme.palette.mode === 'dark'
				? '0px 4px 12px rgba(0, 0, 0, 0.3)'
				: '0px 4px 12px rgba(26, 26, 26, 0.15)',
		},
	},
	textInput: {
		background: (theme) => theme.palette.mode === 'dark'
			? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.06))'
			: 'linear-gradient(135deg, rgba(45, 74, 45, 0.08), rgba(45, 74, 45, 0.04))',
		outline: 'none',
		padding: '1rem 1.5rem',
		borderRadius: '12px',
		border: (theme) => theme.palette.mode === 'dark'
			? '1px solid rgba(45, 74, 45, 0.4)'
			: '1px solid rgba(45, 74, 45, 0.2)',
		boxShadow: (theme) => theme.palette.mode === 'dark'
			? '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px rgba(255, 255, 255, 0.1)'
			: '0px 4px 16px rgba(45, 74, 45, 0.08), inset 0px 1px 0px rgba(255, 255, 255, 0.3)',
		backdropFilter: 'blur(8px)',
	},
};

const CssButton = styled(Button)({
	padding: '.5rem 1rem',
	marginLeft: '1rem !important',
	fontSize: pxToRem(14),
	...responsiveFontSizes({ sm: 16, md: 18, lg: 26 }),
	'&.Mui-disabled': {
		color: 'rgba(0, 0, 0, 0.26)',
		backgroundColor: '#d8d8d8',
	},
});

const CssMatchButton = styled(Button)(({ theme }) => ({
	padding: '0.875rem 2rem', // Slightly larger padding for better presence
	marginLeft: '1.5rem !important', // Better alignment spacing
	fontSize: pxToRem(14),
	...responsiveFontSizes({ sm: 14, md: 16, lg: 18 }),
	fontWeight: 700, // Bolder font for better visibility
	textTransform: 'none',
	borderRadius: '14px', // Slightly more rounded for professional look
	background: '#2E7D32', // Professional dark green - highly visible
	color: '#ffffff',
	border: '2px solid #2E7D32', // Thicker border for definition
	boxShadow: theme.palette.mode === 'dark'
		? '0px 6px 20px rgba(46, 125, 50, 0.5), inset 0px 1px 0px rgba(255, 255, 255, 0.2)'
		: '0px 6px 20px rgba(46, 125, 50, 0.4), inset 0px 1px 0px rgba(255, 255, 255, 0.3)',
	transition: 'all 0.3s ease',
	letterSpacing: '0.5px', // Better text spacing
	'&:hover': {
		background: '#4CAF50', // Brighter green on hover
		borderColor: '#4CAF50',
		transform: 'translateY(-2px)', // More pronounced hover effect
		boxShadow: theme.palette.mode === 'dark'
			? '0px 8px 24px rgba(76, 175, 80, 0.6)'
			: '0px 8px 24px rgba(76, 175, 80, 0.5)',
	},
	'&:active': {
		transform: 'translateY(-1px)',
		background: '#1B5E20', // Darker green when pressed
		borderColor: '#1B5E20',
	},
	'&.Mui-disabled': {
		color: 'rgba(255, 255, 255, 0.6)',
		backgroundColor: theme.palette.mode === 'dark' 
			? 'rgba(46, 125, 50, 0.3)' 
			: 'rgba(46, 125, 50, 0.2)',
		border: theme.palette.mode === 'dark'
			? '2px solid rgba(46, 125, 50, 0.3)'
			: '2px solid rgba(46, 125, 50, 0.2)',
		boxShadow: 'none',
		transform: 'none',
	},
}));

const UpdateSerialNo = ({ selectedRows, handleClose, showMessageOverlay }) => {
	const handleSubmit = () => {
		const folderName = encodeURIComponent(folder_name);
		const fileName = `${encodeURIComponent(file_name)}.${encodeURIComponent(selectedRows[0].labels[0].extension)}`;
		const content = encodeURIComponent(selectedRows[0].labels[0].value);
		window.location.href = `nirmaan://run?cmd=create_file&folder=${folderName}&file=${fileName}&content=${content}`;
		// 	folderName,
		// 	fileName,
		// 	content,
		// 	(message) => {
		// 		handleClose();
		// 		const closeOverlay = showMessageOverlay({
		// 			contextText: `Reengraving File generated successfully `,
		// 			success: true,
		// 		});
		// 		setTimeout(() => {
		// 			closeOverlay();
		// 		}, 3000);
		// 	},
		// 	(error) => {
		// 		handleClose();
		// 		showMessageOverlay({
		// 			contextText: ENGRAVING_FILE_CREATION_ERROR.ERROR_CONTEXT_TEXT,
		// 			info: error?.response?.data?.message,
		// 			error: true,
		// 		});
		// 	},
		// );
	};
	return (
		<Grid
			container
			paddingX={3}
			paddingTop={1}
			paddingBottom={2}
			alignItems="end"
			gap={2}
			justifyContent="flex-end"
		>
			<LargeButton
				backgroundColor="#82C1A7"
				name="CANCEL"
				handleButtonClick={handleClose}
				test-id="CANCEL"
			/>
			<LargeButton
				backgroundColor="#82C1A7"
				name="SUBMIT"
				handleButtonClick={handleSubmit}
				disabled={selectedRows.length === 1 ? false : true}
				test-id="SUBMIT"
			/>
		</Grid>
	);
};

const ReengraveModal = ({ handleClose }) => {
	const [serialNumber, setSerialNumber] = useState('');
	const { showMessageOverlay } = useMessageContextManager();
	const [getSerialNumber, setGetSerialNumber] = useState([]);

	const onSubmitButtonClick = () => {
		axiosInstance
			.get(`/station/label-reprint/${serialNumber}?types=ENGRAVE`)
			.then(({ data }) => {
				const rowsData = data.map((item) => ({
					...item,
					serialNumber: item.serialNumber,
					id: item.serialNumber,
				}));
				setGetSerialNumber(rowsData);
			})
			.catch((error) => {
				showMessageOverlay({
					contextText: PRINTER_ERROR.ERROR_CONTEXT_TEXT + 'Engaving',
					info: error?.response?.data?.message,
					error: true,
				});
			});
	};

	return (
		<Dialog
			open={true}
			sx={classNames.dialogCointainer}
			PaperProps={{ sx: classNames.dialog }}
		>
			<DialogTitle sx={{ 
				display: 'flex', 
				alignItems: 'center',
				padding: '2.5rem 2.5rem 1.5rem 2.5rem', // More balanced padding
				gap: 1, // Consistent spacing between elements
			}}>
				<Typography 
					variant="h4" 
					sx={{
						color: (theme) => theme.palette.mode === 'dark'
							? '#ffffff' // Pure white for dark mode - maximum contrast
							: '#1a1a1a', // Near black for light mode - maximum contrast
						fontWeight: 800,
						letterSpacing: '1.2px',
						textShadow: (theme) => theme.palette.mode === 'dark'
							? '0px 2px 4px rgba(0, 0, 0, 0.5)' // Stronger shadow for dark mode
							: '0px 2px 4px rgba(0, 0, 0, 0.2)', // Stronger shadow for light mode
						// Enhanced text stroke for better visibility
						WebkitTextStroke: (theme) => theme.palette.mode === 'dark'
							? '0.5px rgba(45, 74, 45, 0.3)'
							: '0.5px rgba(45, 74, 45, 0.1)',
						fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem' },
					}}
				>
					Re Engrave
				</Typography>
				<Box sx={classNames.titleLine} />
				<IconButton
					sx={{ 
						color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
						background: (theme) => theme.palette.mode === 'dark'
							? 'rgba(255, 255, 255, 0.1)'
							: 'rgba(26, 26, 26, 0.05)',
						borderRadius: '12px',
						padding: '0.75rem',
						transition: 'all 0.3s ease',
						'&:hover': {
							background: (theme) => theme.palette.mode === 'dark'
								? 'rgba(255, 255, 255, 0.2)'
								: 'rgba(26, 26, 26, 0.1)',
							transform: 'scale(1.05)',
						}
					}}
					onClick={handleClose}
				>
					<CloseIcon sx={{ width: '24px', height: '24px' }} />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<Box sx={classNames.dialogContent}>
					<Typography
						sx={{ ...classNames.noteHeading }}
						paddingTop={2}
						paddingLeft={2}
					>
						Note: Re engrave is only available for Booked, Taken out and In
						flight parts
					</Typography>
					<Box sx={classNames.content}>
						<Grid display="flex" direction="row" width="100%">
							<Grid container direction="column" spacing={2}>
								<Grid item container direction="column">
									<Grid item>
										<Typography
											sx={{ ...classNames.heading, ...classNames.item }}
										>
											Enter Last 5 Digits
										</Typography>
									</Grid>
									<Grid item display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
										<Grid xs={9} sx={{ display: 'flex', alignItems: 'center' }}>
											<TextField
												fullWidth
												autoFocus
												type="text"
												placeholder="Enter Last 5 Digits"
												variant="standard"
												sx={{
													...classNames.textInput,
													minHeight: '56px', // Consistent height with button
												}}
												value={serialNumber}
												onChange={(e) => {
													const sanitizedValue = e.target.value.replace(
														forbiddenChars,
														'',
													).toUpperCase();
													setSerialNumber(sanitizedValue);
												}}
												InputProps={{
													style: { 
														fontSize: 16,
														height: '56px', // Match button height
														display: 'flex',
														alignItems: 'center',
													},
													disableUnderline: true,
												}}
											/>
										</Grid>
										<Grid xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
											<CssMatchButton
												size="medium"
												variant="contained"
												disabled={!serialNumber}
												onClick={onSubmitButtonClick}
												sx={{ minWidth: '120px' }} // Consistent button width
											>
												Submit
											</CssMatchButton>
										</Grid>
									</Grid>
									{getSerialNumber.length > 0 && (
										<Grid item marginTop={2}>
											<BasicTable
												showCheckBox
												showRowCount
												columns={[...DEFAULT_COLUMNS]}
												customiseTable={false}
												tableStyles={{
													tableContainer: classNames.tableContainer,
												}}
												rows={[...getSerialNumber]}
												subtitle="Serial Number"
												defaultColumns={[...DEFAULT_COLUMNS]}
											>
												{(selectedRows) => (
													<UpdateSerialNo
														selectedRows={selectedRows}
														handleClose={handleClose}
														showMessageOverlay={showMessageOverlay}
													/>
												)}
											</BasicTable>
										</Grid>
									)}
								</Grid>
							</Grid>
						</Grid>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ReengraveModal;
